import * as React from 'react'
import {Box, Typography} from '@mui/material'
import {kpiLabelSx} from '../../../../../styles/patterns'
import {FONT, SIZE, WEIGHT} from '../../../../../styles/foundation/typography'
import {NAVY, SUBTLE} from '../../../../../styles/foundation/colors'
import {toneColor} from '../../components/tone'

/**
 * One small labelled statistic — "Chargeable 1.35 kg", "Freeze covers
 * 4 packs", "Volumetric 0.86 kg / dead 1.35 kg". Packing shows a lot of
 * these: every pack row, every comparison panel and every drawer header
 * carries several, so this is deliberately the cheapest possible unit of
 * layout rather than another card.
 *
 * Density is the whole point. It is NOT a KPI card: no border, no
 * background, no `kpiCardSx` (whose `p: 2.5`, `SIZE['3xl']` value and
 * `height: '100%'` are what made the SoP tiles too airy before they were
 * tightened — see SopPage.js's local `p: 1.75, height: 'auto'` override
 * and its step down to `SIZE['2xl']`). A Figure steps down once more
 * again: `SIZE.lg` value, `SIZE['2xs']` label. It sizes to its own
 * content and inherits whatever card, row or table cell it is dropped
 * into, so a screen can put six of them in a flex row without six
 * competing boxes. `minWidth: 0` so it shrinks in that row instead of
 * forcing the row to scroll.
 *
 * The label reuses `kpiLabelSx` (uppercase, tracked, muted) so a Figure
 * and a real KPI tile read as the same family, only smaller.
 *
 * `tone` is optional and is a tone NAME from components/tone.js
 * (ok / warn / info / neutral / danger / ai), resolved through
 * `toneColor` — the module never hardcodes a hex. It tints the value
 * only; the label and sub-line stay muted so a row of figures where one
 * is `danger` reads as one figure standing out, not as two colour
 * schemes. With no tone the value is plain NAVY body text, which is the
 * common case — most figures are just numbers, not judgements.
 */
export default function Figure({label, value, sub, tone}) {
  return (
    <Box sx={{minWidth: 0}}>
      <Typography sx={{...kpiLabelSx, fontSize: SIZE['2xs'], mb: 0.25}}>
        {label}
      </Typography>
      <Typography
        sx={{
          fontFamily: FONT,
          fontWeight: WEIGHT.bold,
          fontSize: SIZE.lg,
          lineHeight: 1.25,
          color: tone ? toneColor(tone) : NAVY,
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {value}
      </Typography>
      {sub && (
        <Typography sx={{fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE, lineHeight: 1.4, mt: 0.25}}>
          {sub}
        </Typography>
      )}
    </Box>
  )
}
